import { useContext, useMemo } from 'react';

import { replaceAll } from 'libs/string';

import { ContentContext } from 'context/ContentContext';

import { Testimonials } from 'ui/organisms/Testimonials/Testimonials';
import { UseCases } from 'ui/organisms/UseCases/UseCases';
import { Hero } from 'ui/organisms/Hero/Hero';
import { VideoOverview } from 'ui/organisms/VideoOverview/VideoOverview';
import { BlockStandards } from 'ui/molecules/BlockStandards/BlockStandards';
import { OurAwards } from 'ui/molecules/OurAwards/OurAwards';
import { OurCustomersV2 } from 'ui/molecules/OurCustomers/OurCustomersV2';
import { StatsBlock } from 'ui/molecules/StatsBlock/StatsBlock';
import { InfoBlocks } from 'ui/molecules/InfoBlocks/InfoBlocks';
import { Icon } from 'ui/atoms/Icon/Icon';

import { PricingBlock } from 'features/PAYG/organisms/PricingBlock/PricingBlock';
import { SalesBanner } from 'features/PAYG/organisms/SalesBanner/SalesBanner';

import styles from './PAYGSecondPage.module.scss';

import { Faq } from '../../organisms/Faq';
import { PAYG_SECOND_PAGE_FAQ_DATA, PAYG_SECOND_PAGE_FAQ_DESCRIPTION, PAYG_SECOND_PAGE_FAQ_TITLE } from './faqData';

const VIDEO_ID = '0kj0iMuZ3iA';

const defaultProductTypes = ['payg2_starter', 'payg2_professional', 'payg2_business'];

const baseClass = 'PAYG-second-page';

const PAYGSecondPage = () => {
  const { data: content, internationalization } = useContext(ContentContext);

  const [hasHeroExpanded, hasLeadPricing, hasSwitchReasonLightDark, hasDataCasesLight, hasPlainInfoBlock] = useMemo(
    () => [
      content.blocks.includes('hero-expanded'),
      content.blocks.includes('lead-pricing'),
      content.blocks.includes('switch-reason-light-dark'),
      content.blocks.includes('data-cases-light'),
      content.blocks.includes('info-block-plain'),
    ],
    [content.blocks],
  );

  const productTypes = useMemo(() => {
    if (content.blocks.includes('pricing-pipeline')) {
      return ['payg_pipeline_starter', 'payg_pipeline_professional', 'payg_pipeline_business'];
    }

    return defaultProductTypes;
  }, [content.blocks]);

  const dataCases = useMemo(
    () => [
      {
        title: internationalization['usecases_1_title'],
        description: internationalization['usecases_1_description'],
        icon:
          hasDataCasesLight &&
          ['payg-government', 'payg-source', 'payg-rights'].includes(internationalization['usecases_1_icon'])
            ? `${internationalization['usecases_1_icon']}-light`
            : internationalization['usecases_1_icon'],
      },
      {
        title: internationalization['usecases_2_title'],
        description: internationalization['usecases_2_description'],
        icon:
          hasDataCasesLight &&
          ['payg-government', 'payg-source', 'payg-rights'].includes(internationalization['usecases_2_icon'])
            ? `${internationalization['usecases_2_icon']}-light`
            : internationalization['usecases_2_icon'],
      },
      {
        title: internationalization['usecases_3_title'],
        description: internationalization['usecases_3_description'],
        icon:
          hasDataCasesLight &&
          ['payg-government', 'payg-source', 'payg-rights'].includes(internationalization['usecases_3_icon'])
            ? `${internationalization['usecases_3_icon']}-light`
            : internationalization['usecases_3_icon'],
      },
    ],
    [hasDataCasesLight, internationalization],
  );

  const dataTestimonial = useMemo(
    () => [
      {
        quote:
          "When you're resolving entities and mapping risk across borders, the foundation has to be authoritative and traceable — a registry-sourced record we can stand behind when a decision is questioned. Global Database gives us that across the markets we're expanding into, and their team absorbs the registry-level differences and constant changes, so ours stays focused on the intelligence layer rather than the data plumbing.",
        name: 'Adam Mulliken',
        role: 'CPO, Quantifind',
      },
      {
        quote:
          "We use Global Database's credit risk data when verifying and onboarding customers. It gives our teams the company and financial information they need to assess risk, complete checks and make decisions with confidence.",
        name: 'Carol Zhang',
        role: 'Associate Vice President, Kroll',
      },
      {
        quote:
          'We use Global Database to enrich our bank account validation and payee checks with business data. It helps us identify the company behind the payment and gives our customers more confidence before they pay.',
        name: 'Alexandre Outman',
        role: 'Lead Product Manager, Trustpair',
      },
      {
        quote:
          'Global Database helps us build a clear picture of our addressable market across the UK and Ireland. With more than 100 criteria available, we can segment companies precisely and map the right opportunities to each of our teams.',
        name: 'Cameron Trayner',
        role: 'Schneider Electric',
      },
      {
        quote:
          "Global Database's shareholder data is an important part of our intelligence graph. It helps us connect individuals to the businesses they own or control, revealing relationships that conventional screening can miss and giving our clients a more complete view of who they are dealing with.",
        name: 'Oli Warriner',
        role: 'Chief Technology Officer, YOONO',
      },
      {
        quote:
          'Our products depend on authoritative company information across multiple jurisdictions. The government registry data provided by Global Database helps us enrich our offering and deliver reliable business information across key European markets.',
        name: 'Wendy Song',
        role: 'BDC Director, Experian',
      },
    ],
    [],
  );

  const dataSwitchReasonInfo = useMemo(
    () => [
      {
        icon: internationalization['info_blocks_1_icon'],
        title: internationalization['info_blocks_title_1'],
        items: [
          internationalization['info_blocks_1_item_1'],
          internationalization['info_blocks_1_item_2'],
          internationalization['info_blocks_1_item_3'],
          internationalization['info_blocks_1_item_4'],
          internationalization['info_blocks_1_item_5'],
          internationalization['info_blocks_1_item_6'],
        ],
      },
      {
        icon: internationalization['info_blocks_2_icon'],
        title: internationalization['info_blocks_title_2'],
        items: [
          internationalization['info_blocks_2_item_1'],
          internationalization['info_blocks_2_item_2'],
          internationalization['info_blocks_2_item_3'],
          internationalization['info_blocks_2_item_4'],
          internationalization['info_blocks_2_item_5'],
          internationalization['info_blocks_2_item_6'],
          internationalization['info_blocks_2_item_7'],
        ],
      },
      {
        icon: internationalization['info_blocks_3_icon'],
        title: internationalization['info_blocks_title_3'],
        items: [
          internationalization['info_blocks_3_item_1'],
          internationalization['info_blocks_3_item_2'],
          internationalization['info_blocks_3_item_3'],
          internationalization['info_blocks_3_item_4'],
          internationalization['info_blocks_3_item_5'],
        ],
      },
      {
        icon: internationalization['info_blocks_4_icon'],
        title: internationalization['info_blocks_title_4'],
        items: [
          internationalization['info_blocks_4_item_1'],
          internationalization['info_blocks_4_item_2'],
          internationalization['info_blocks_4_item_3'],
          internationalization['info_blocks_4_item_4'],
          internationalization['info_blocks_4_item_5'],
          internationalization['info_blocks_4_item_6'],
          internationalization['info_blocks_4_item_7'],
        ],
      },
      {
        icon: internationalization['info_blocks_5_icon'],
        title: internationalization['info_blocks_title_5'],
        items: [
          internationalization['info_blocks_5_item_1'],
          internationalization['info_blocks_5_item_2'],
          internationalization['info_blocks_5_item_3'],
          internationalization['info_blocks_5_item_4'],
          internationalization['info_blocks_5_item_5'],
          internationalization['info_blocks_5_item_6'],
          internationalization['info_blocks_5_item_7'],
        ],
      },
      {
        icon: internationalization['info_blocks_6_icon'],
        title: internationalization['info_blocks_title_6'],
        items: [
          internationalization['info_blocks_6_item_1'],
          internationalization['info_blocks_6_item_2'],
          internationalization['info_blocks_6_item_3'],
          internationalization['info_blocks_6_item_4'],
          internationalization['info_blocks_6_item_5'],
          internationalization['info_blocks_6_item_6'],
        ],
      },
      {
        icon: internationalization['info_blocks_7_icon'],
        title: internationalization['info_blocks_title_7'],
        items: [internationalization['info_blocks_7_item_1']],
      },
      {
        icon: internationalization['info_blocks_8_icon'],
        title: internationalization['info_blocks_title_8'],
        items: [internationalization['info_blocks_8_item_1']],
      },
      {
        icon: internationalization['info_blocks_9_icon'],
        title: internationalization['info_blocks_title_9'],
        items: [internationalization['info_blocks_9_item_1']],
      },
    ],
    [internationalization],
  );

  const statsFeatures = useMemo(
    () => [
      {
        value: internationalization['stats_block_feature_1_value'],
        suffix: internationalization['stats_block_feature_1_suffix'],
        title: internationalization['stats_block_feature_1_title'],
      },
      {
        value: internationalization['stats_block_feature_2_value'],
        suffix: internationalization['stats_block_feature_2_suffix'],
        title: internationalization['stats_block_feature_2_title'],
      },
      {
        value: internationalization['stats_block_feature_3_value'],
        suffix: internationalization['stats_block_feature_3_suffix'],
        title: internationalization['stats_block_feature_3_title'],
      },
      {
        value: internationalization['stats_block_feature_4_value'],
        suffix: internationalization['stats_block_feature_4_suffix'],
        title: internationalization['stats_block_feature_4_title'],
      },
    ],
    [internationalization],
  );

  const salesBannerTags = [
    internationalization['sales_banner_tag_1'],
    internationalization['sales_banner_tag_2'],
    internationalization['sales_banner_tag_3'],
    internationalization['sales_banner_tag_4'],
    internationalization['sales_banner_tag_5'],
  ];

  const filteredFaqData = useMemo(() => {
    const internationalizationKey = content.internationalization_key;

    if (!internationalizationKey) {
      return PAYG_SECOND_PAGE_FAQ_DATA;
    }

    return PAYG_SECOND_PAGE_FAQ_DATA.filter((item) => {
      if (item.internationalizations) {
        return item.internationalizations.includes(internationalizationKey);
      }

      return true;
    });
  }, [content.internationalization_key]);

  return (
    <div className={styles[`${baseClass}-wrapper`]}>
      <Hero
        kicker={
          content.internationalization_key && (
            <Icon type={replaceAll(content.internationalization_key, '_', '-')} style={{ fontSize: 72 }} />
          )
        }
        title={content['title']}
        description={content['description']}
        footer={<StatsBlock theme="dark" hasPadding={false} hasBackground={false} features={statsFeatures} />}
        {...(hasHeroExpanded && {
          hasGDBBackground: true,
          rightSide: <div className={styles[`${baseClass}-hero-right-side`]} />,
        })}
      />

      {hasLeadPricing && (
        <>
          <PricingBlock
            id="pricing"
            extra={internationalization['pricing_block_extra']}
            title={internationalization['pricing_block_title']}
            description={internationalization['pricing_block_description']}
            hasFeatureKeys={true}
            productTypes={productTypes}
          />

          <OurCustomersV2 />
        </>
      )}

      {hasLeadPricing && (
        <SalesBanner
          theme="light"
          title={internationalization['sales_banner_title']}
          description={internationalization['sales_banner_description']}
          buttonText={internationalization['sales_banner_button_text']}
          tags={salesBannerTags}
        />
      )}

      <InfoBlocks
        theme={hasSwitchReasonLightDark ? 'light-dark' : 'light'}
        layout="column"
        extra={internationalization['info_blocks_extra']}
        title={internationalization['info_blocks_title'] || 'Comprehensive Company Intelligence'}
        description={
          internationalization['info_blocks_description'] ||
          'From basic business details to deep organizational insights—everything you need to understand any company.'
        }
        hasCheckItems={!hasPlainInfoBlock}
        items={dataSwitchReasonInfo}
      />

      <UseCases
        extra="WHY GLOBAL DATABASE"
        title={internationalization['usecases_title']}
        description={internationalization['usecases_description']}
        dataCases={dataCases}
        layout="grid-3-3"
        largeIcons
        hasHover={false}
        theme={hasDataCasesLight ? 'light' : undefined}
      />

      {!hasLeadPricing && (
        <>
          <PricingBlock
            id="pricing"
            extra={internationalization['pricing_block_extra']}
            title={internationalization['pricing_block_title']}
            description={internationalization['pricing_block_description']}
            hasFeatureKeys={true}
            productTypes={productTypes}
          />

          <SalesBanner
            title={internationalization['sales_banner_title']}
            description={internationalization['sales_banner_description']}
            buttonText={internationalization['sales_banner_button_text']}
            tags={salesBannerTags}
          />

          <OurCustomersV2 />
        </>
      )}

      <OurAwards
        title="Stay Ahead with Global Database Excellence and Recognition"
        description="We Adhere to the Industry's Highest Standards of Excellence"
        variant="split"
      />

      <VideoOverview
        title="Watch a 5-min video presentation of our global company information database"
        description="Lead generation and stockholders’ timely due diligence form the essence of any business’s security and continuity"
        videoId={VIDEO_ID}
      />

      <Testimonials
        title={internationalization['testimonials_title']}
        description={internationalization['testimonials_description']}
        data={dataTestimonial}
        layout="compact"
      />

      <BlockStandards title="We Adhere to the Industry’s Highest Standards of Excellence" theme="light" />

      <Faq title={PAYG_SECOND_PAGE_FAQ_TITLE} description={PAYG_SECOND_PAGE_FAQ_DESCRIPTION} data={filteredFaqData} />
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default PAYGSecondPage;
